import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { formatElapsed, useNowSecond } from '@shared/lib/elapsed';
import { useActiveWorkout } from '@features/workouts/application/use-workouts';
import { SyncIndicator } from '@features/workouts/presentation/SyncIndicator';

/**
 * Sticky pill above page content showing the currently-running workout. Lets
 * the user jump back into the session from any tab (Programs / Progress /
 * Profile) without losing the live timer. Hidden on the workout page itself
 * — the session screen already shows everything.
 */
export function ActiveSessionBar(): React.JSX.Element | null {
  const activeQuery = useActiveWorkout();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const active = activeQuery.data;
  if (!active) return null;
  // On the session page itself the bar is redundant. /workout/new is the
  // chooser — keep the bar there so the user can bail back to the running
  // session.
  if (pathname.startsWith('/workout/') && pathname !== '/workout/new') return null;

  return (
    <ActiveSessionPill startedAt={active.startedAt} sessionId={active.id} title={active.title} />
  );
}

function ActiveSessionPill({
  startedAt,
  sessionId,
  title,
}: {
  startedAt: string;
  sessionId: string;
  title: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const now = useNowSecond(true);
  const elapsedMs = Math.max(0, now - new Date(startedAt).getTime());
  return (
    <Link
      to="/workout/$sessionId"
      params={{ sessionId }}
      className={cn(
        'border-brand/30 bg-brand/5 hover:bg-brand/10 sticky top-0 z-20',
        'flex items-center gap-3 border-b px-4 py-2 backdrop-blur-xl',
        'pt-[max(0.5rem,env(safe-area-inset-top))]',
        'transition-colors focus-visible:outline-none',
      )}
      aria-label={t('home.continueWorkout')}
    >
      <span
        className="bg-brand inline-block size-2 shrink-0 animate-pulse rounded-full"
        aria-hidden
      />
      <span className="text-foreground text-caption flex-1 truncate font-medium">
        {title ?? t('workouts.session.defaultTitle')}
      </span>
      <span className="text-brand text-caption font-mono tabular-nums" aria-live="polite">
        {formatElapsed(elapsedMs)}
      </span>
      <SyncIndicator />
      <ChevronRight className="text-brand size-4 shrink-0" aria-hidden />
    </Link>
  );
}
